import React, { FC, useCallback, useRef, useState } from 'react';
import compact from 'lodash/compact';
import { IAutocompleteOption, IAutocompleteComponent } from '@tylertech/forge';
import { ForgeAutocomplete, ForgeIcon, ForgeIconButton, ForgeTextField } from '@tylertech/forge-react';

import { AlloyComponentDataTestIdKeys, AlloyDefaultProps } from 'common/AlloyCandidates/types';
import { makeGetRequest } from 'common/catalog-service-api';
import { API_URL, AUTOCOMPLETE_API_URL } from 'common/teams-api';
import { CatalogTeam, UsersCatalogSearchResults } from 'common/types/users/catalogUsers';
import getScopedTranslateMethod from 'common/js_utils/getScopedTranslateMethod';
import { isArray, sortBy } from 'lodash';
import useAlloySetup from 'common/AlloyCandidates/helpers/useAlloySetup';

/** A CatalogTeam object formated for ForgeAutocomplete */
export type TeamAutocompleteOption = IAutocompleteOption<CatalogTeam>;
/**
 * List of data-testid "slots" specific to this Alloy Component
 * extended with the universal Alloy Component slots
 * */
export type TeamAutocompleteDataTestIdKeys = AlloyComponentDataTestIdKeys<
  'input' | 'label' | 'button' | 'icon-clear' | 'icon-dropdown'
>;
/**
 * Props specific to this Alloy Component
 * extended with the universal Alloy Component props
 */
export interface TeamAutocompleteProps
  extends AlloyDefaultProps<TeamAutocompleteDataTestIdKeys, IAutocompleteComponent> {
  /** Method to make selected options available to parent component.
   *  - Will receive an array of the selected CatalogTeam objects.
   *  - Fires when the autocomplete loses focus.
   */
  updateSelections: (selectedOptions: CatalogTeam[]) => void;

  /** Hide the label */
  hideLabel?: boolean;
}

/**
 * Transforms a CatalogTeam object to a TeamAutocompleteOption object
 * @param team Team object to transform into the format needed for ForgeAutocomplete
 * @returns TeamAutocompleteOption
 */
export const createTeamAutoCompleteOption = (team: CatalogTeam): TeamAutocompleteOption => {
  return { value: team, label: team.screen_name };
};

export const isUsersCatalogSearchResult = (
  result: UsersCatalogSearchResults | CatalogTeam
): result is UsersCatalogSearchResults => {
  return !!(result?.hasOwnProperty('team') && (result as UsersCatalogSearchResults).team);
};

/** This Alloy Component's specific prefix. Used for ids, data-testids, etc */
export const componentPrefix = 'alloy-team-autocomplete';
/** Translation method scoped to this specific Alloy Component */
export const translate = getScopedTranslateMethod('common.alloy.team_autocomplete');

/**
 * The TeamAutocomplete Alloy Component (React Function Component)
 * @param props TeamAutocompleteProps object
 * @returns JSX.Element | null
 */
export const TeamAutocomplete: FC<TeamAutocompleteProps> = (props: TeamAutocompleteProps) => {
  const { updateSelections, hideLabel } = props;
  const ref = useRef<typeof ForgeAutocomplete & { value: CatalogTeam[] }>();

  const [selected, setSelected] = useState<TeamAutocompleteOption[]>([]);

  /**
   * Passed to the ForgeAutocomplete's filter prop, queries catalog API for matching teams
   * @param query The query string to pass to the API
   * @param selectOptions The list of already selected teams
   * @returns Promise<TeamAutocompleteOption[]>
   */
  const queryTeams = useCallback(
    async (query: string, selectedOptions: TeamAutocompleteOption[] = []) => {
      let url = API_URL;
      const params: Record<string, string | number | undefined> = {
        domain: window.serverConfig?.domain
      };

      // If we have a query string, use the autocomplete URL and include the query param
      if (query) {
        url = AUTOCOMPLETE_API_URL;
        params.q = query;
      }

      // Retrieve teams
      return await makeGetRequest(url, params).then(({ results }) => {
        // Generate the results by putting our selected teams first in the order
        //  and then listing any non-selected records from results after.
        const response = selectedOptions.concat(
          // Sort the resulting list by the label value
          sortBy(
            // Remove any results which are part of selectedOptions
            compact(
              results.map((result: UsersCatalogSearchResults | CatalogTeam) => {
                // The type casting is less than ideal, but doesn't hurt anything
                const team: CatalogTeam = isUsersCatalogSearchResult(result)
                  ? (result.team as CatalogTeam)
                  : result;

                return team && !selectedOptions.find((option) => option.value.id === team.id)
                  ? createTeamAutoCompleteOption(team)
                  : undefined;
              })
            ),
            'label'
          )
        );

        return response;
      });
    },
    [createTeamAutoCompleteOption]
  );

  /**
   * Pulls the currently selected values from the ForgeAutocomplete and sets them in local state
   */
  const onSelect = () => {
    const selectedValues = ref.current?.value;

    // The selected values will be an array of CatalogTeam objects,
    // so we need to transform each back to a TeamAutocompleteOption object.
    if (selectedValues && isArray(selectedValues))
      setSelected(selectedValues.map((t) => createTeamAutoCompleteOption(t)));
  };

  /**
   * Takes the selected teams and formats a summary string to display to the user
   * @param selectedOptions Array of selected team options
   * @returns String
   */
  const selectedTextBuilder = (selectedOptions: TeamAutocompleteOption[] = []): string => {
    // Default to showing an empty string
    let response = '';

    if (selectedOptions.length) {
      // If there is only 1 selected, show the team name.
      // Otherwise show the count selected.
      response =
        selectedOptions.length === 1
          ? selectedOptions[0].label
          : translate('number_selected', { count: selectedOptions.length });
    }

    return response;
  };

  /**
   * Empties the list of selected teams in both local and store state
   */
  const clearSelection = useCallback(() => {
    setSelected([]);
    updateSelections([]);
  }, [updateSelections, setSelected]);

  /**
   * Passes the list of selected team options to the parent component's provided handler
   */
  const onBlur = useCallback(() => {
    const selectedOptions = ref.current?.value;

    // The selected values will be an array of CatalogTeam objects,
    // so we need to transform each back to a TeamAutocompleteOption object.
    if (selectedOptions && isArray(selectedOptions)) updateSelections(selectedOptions);
  }, [updateSelections]);

  /** Default props which will be passed to the ForgeAutocomplete component */
  const defaultForgeProps = {
    ref: ref,
    allowUnmatched: false,
    filterOnFocus: true,
    multiple: true,
    filter: (query: string) => queryTeams(query, selected),
    onSelect: onSelect,
    selectedTextBuilder: selectedTextBuilder,
    syncPopupWidth: true,
    value: selected
  };

  /** Alloy Setup */
  const { getId, forgeProps } = useAlloySetup<
    TeamAutocompleteDataTestIdKeys,
    TeamAutocompleteProps,
    IAutocompleteComponent
  >(componentPrefix, props, defaultForgeProps);

  return window.serverConfig?.domain ? (
    <ForgeAutocomplete {...forgeProps} data-testid={getId('main')}>
      <ForgeTextField>
        <input
          data-testid={getId('input')}
          type="text"
          id={getId('input')}
          onBlur={() => onBlur()}
          placeholder={translate('search')}
        />
        {!hideLabel && (
          <label htmlFor={getId('input')} data-testid={getId('label')}>
            {translate('team')}
          </label>
        )}
        {!!selected.length && (
          <ForgeIconButton
            slot="end"
            data-forge-autocomplete-clear
            onclick={() => clearSelection()}
            title={translate('clear_selected')}
            aria-label={translate('clear_selected')}
          >
            <ForgeIcon name="close" data-testid={getId('icon-clear')} />
          </ForgeIconButton>
        )}
        <ForgeIcon
          slot="end"
          data-forge-dropdown-icon
          name="arrow_drop_down"
          data-testid={getId('icon-dropdown')}
        />
      </ForgeTextField>
    </ForgeAutocomplete>
  ) : null;
};

export default TeamAutocomplete;
